import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import {
    DataGridPro,
    GridColDef,
    GridColumnVisibilityModel,
    GridFilterInputSingleSelect,
    GridFilterOperator,
    GridPaginationModel,
    GridSortModel,
    GridToolbarContainer,
    useGridApiContext,
} from '@mui/x-data-grid-pro';
import Iconify from 'src/components/iconify';
import CustomTooltip from 'src/components/visibility/components/CustomTooltip';
import type { InfringementRow } from 'src/context/api/brandInfringement/api';
import {
    ActiveDaysGaugeCell,
    PaidPresenceRateCell,
    PLACE_OPTIONS,
    placeLabels,
    presenceDisplayValue,
    PlaceChipsCell,
    SearchTermsCell,
    ReportCell,
} from './cells';

const ROW_HEIGHT = 44;

// A row can infringe in several places at once, so its value is a list of labels and equality is
// the wrong test: "is Title" has to match a row showing both Title and Title + Description. MUI's
// own singleSelect operators compare with `===`, which silently matched nothing here.
const placeFilterOperators: GridFilterOperator[] = [
    {
        label: 'is',
        value: 'is',
        getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) return null;
            return (value) => Array.isArray(value) && value.includes(filterItem.value);
        },
        InputComponent: GridFilterInputSingleSelect,
    },
    {
        label: 'is not',
        value: 'not',
        getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) return null;
            return (value) => !(Array.isArray(value) && value.includes(filterItem.value));
        },
        InputComponent: GridFilterInputSingleSelect,
    },
];

// `__busy` rides on the row, not on the columns: see `columns` below.
type InfringementRowWithId = InfringementRow & { __rowId: string; __busy: boolean };

type Props = {
    rows: InfringementRow[];
    loading: boolean;
    onTermClick: (term: string, row?: InfringementRow) => void;
    /** A click on a search terms cell, outside its chips: the row's Ads, every term at once. */
    onTermsCellClick: (row: InfringementRow) => void;
    onDownloadDomainReport: (row: InfringementRow) => void;
    onExportAll: () => void;
    rightSlot?: ReactNode;
    busyDomain?: string | null;
    /** Covers the whole card with a skeleton block instead of showing a spinner over the grid. */
    showSkeleton?: boolean;
};

// Measured in the browser: toolbar (42 + 8 margin) + column headers (36) + a full page of 10 rows
// (440) + footer (40) + the frame's borders (2).
const CARD_HEIGHT = 568;

const DIVIDER = '1px solid rgba(145, 158, 171, 0.2)';

// Given to DataGridPro's own `sx`, not to the frame around it: MUI's root styles (`0 10px` padding on
// cells and headers, its own pale border colour) carry the same specificity as a parent's
// `& .MuiDataGrid-*` rule, so whichever stylesheet emotion inserted last won, and the design QA
// measured MUI's defaults. The grid merges its `sx` after its own styles, so these always apply.
const GRID_SX = {
    // Bottom border of the header row and of every row (Figma: #919EAB33).
    '--DataGrid-rowBorderColor': 'rgba(145, 158, 171, 0.2)',
    // v7 paints the header through this variable; `.MuiDataGrid-columnHeaders` has no effect.
    '--DataGrid-containerBackground': 'rgba(226,229,233,0.16)',
    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
        outline: 'none !important',
    },
    '& .MuiDataGrid-cell': {
        display: 'flex',
        alignItems: 'center',
        // Same inset as the column headers below, so a title sits exactly above its own column's
        // content.
        px: '12px',
        borderRight: DIVIDER,
        // The theme paints cell bottom borders dashed (overrides/components/data-grid.tsx), and v7
        // separates rows with a top border of its own: together they drew two lines between rows.
        // One solid bottom border, as in the Figma.
        borderTop: 'none',
        borderBottom: DIVIDER,
    },
    // The footer's top border closes the table: the last row's own would double it.
    '& .MuiDataGrid-row--lastVisible .MuiDataGrid-cell': {
        borderBottom: 'none',
    },
    // The chips in this column deep-link into Ads, so the cell announces itself on
    // hover. Scoped to the cell rather than the row: the rest of the row isn't
    // clickable. The tint is translucent, so it composites over the white row.
    '& .MuiDataGrid-cell[data-field="search_terms"]:hover': {
        backgroundColor: 'rgba(226,229,233,0.16)',
    },
    // v7 closes every row with a filler div that carries the `cell` class (GridRow.js),
    // and adds a scrollbar filler beside the headers. Left alone they inherit the border
    // rules above and draw a stray bordered box past the last column.
    '& .MuiDataGrid-cellEmpty, & .MuiDataGrid-filler, & .MuiDataGrid-scrollbarFiller': {
        border: 'none',
    },
    // The frame is flush against the grid, so the last column's own right border
    // would sit one pixel from the frame border and read as a double line. Anchored on
    // the field rather than `:last-of-type`, which matches those fillers, not a column.
    '& .MuiDataGrid-cell[data-field="status"], & .MuiDataGrid-columnHeader[data-field="status"]': {
        borderRight: 'none',
    },
    // The global data-grid-custom.css forces `white-space: normal !important` on all
    // headers; override it locally so the titles stay on a single line.
    '& .MuiDataGrid-columnHeaderTitle': {
        color: '#637381',
        fontSize: 14,
        fontWeight: 600,
        lineHeight: '16.8px',
        whiteSpace: 'nowrap !important',
        overflow: 'visible',
    },
    // 36px flat, whatever the title does: `columnHeaderHeight` sizes the header row but the header
    // cell itself still grew with its content (the global `white-space: normal` in
    // data-grid-custom.css lets a long title wrap onto a second line). Pinned here, with the
    // Figma's 12px insets.
    '& .MuiDataGrid-columnHeader': {
        height: 36,
        minHeight: 36,
        maxHeight: 36,
        boxSizing: 'border-box',
        padding: '0 12px',
        borderRight: DIVIDER,
    },
    // MUI drops its own horizontal padding into this inner container on some header
    // variants (sortable/filterable), which would add to the 12px above.
    '& .MuiDataGrid-columnHeaderDraggableContainer, & .MuiDataGrid-columnHeaderTitleContainer': {
        padding: 0,
    },
    // The header's right border is the column divider of the design. MUI's resize handle draws a
    // second, paler one on top of it: hidden until hovered, the columns stay resizable.
    '& .MuiDataGrid-columnSeparator': { color: 'transparent' },
    '& .MuiDataGrid-columnSeparator--resizable:hover': { color: '#919EAB' },
    // MUI's footer defaults to 52px (its TablePagination toolbar carries its own
    // min-height). Pinned to 40px like the rest of the platform's grids.
    '& .MuiDataGrid-footerContainer': {
        minHeight: 40,
        height: 40,
        backgroundColor: '#FAFAFA',
        borderTop: '1px solid #EAEAEA',
        '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select, & .MuiTablePagination-input': {
            color: '#919EAB',
            fontSize: 14,
        },
    },
    '& .MuiDataGrid-footerContainer .MuiToolbar-root': {
        minHeight: 40,
    },
    // "Rows per page" selector in a bordered box (Figma). The box is the input root, which also
    // anchors the chevron: the select keeps room on its right for it.
    '& .MuiTablePagination-input': {
        border: DIVIDER,
        borderRadius: '6px',
        padding: '4px 8px',
    },
    '& .MuiTablePagination-select': {
        // MUI pins the select's right padding with a `&&&` selector.
        padding: '0 20px 0 0 !important',
        minHeight: '20px !important',
        lineHeight: '20px',
    },
    '& .MuiTablePagination-selectIcon': {
        right: 4,
    },
} as const;

const iconButtonSx = {
    width: 26,
    height: 26,
    padding: 0,
    color: '#919EAB',
    '&:hover': { backgroundColor: 'action.hover' },
} as const;

function CustomToolbar({ onExportAll, rightSlot }: { onExportAll: () => void; rightSlot?: ReactNode }) {
    const apiRef = useGridApiContext();
    return (
        <GridToolbarContainer
            // The frame is flush, so the toolbar carries its own gutter (like the Non Branded grids).
            // The theme gives every toolbarContainer a dashed bottom border and a neutral background
            // (overrides/components/data-grid.tsx); with the `mb` below, that border floated as a
            // second rule just above the column headers. Both are dropped here.
            sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                p: 0, px: 2, pt: '10px', mb: 1,
                border: 'none', backgroundColor: 'transparent',
            }}
        >
            <Stack direction="row" spacing={0.5}>
                <CustomTooltip title="Select columns" arrow placement="top">
                    <IconButton onClick={() => apiRef.current?.showPreferences('columns' as any)} sx={iconButtonSx}>
                        <Iconify icon="mingcute:calendar-week-line" width={18} height={18} />
                    </IconButton>
                </CustomTooltip>
                <CustomTooltip title="Filters" arrow placement="top">
                    <IconButton onClick={() => apiRef.current?.showFilterPanel()} sx={iconButtonSx}>
                        <Iconify icon="ic:round-filter-list" width={18} height={18} />
                    </IconButton>
                </CustomTooltip>
                <CustomTooltip title="Download" arrow placement="top">
                    <IconButton onClick={onExportAll} sx={iconButtonSx}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path
                                d="M14 10V10.8C14 11.9201 14 12.4802 13.782 12.908C13.5903 13.2843 13.2843 13.5903 12.908 13.782C12.4802 14 11.9201 14 10.8 14H5.2C4.07989 14 3.51984 14 3.09202 13.782C2.71569 13.5903 2.40973 13.2843 2.21799 12.908C2 12.4802 2 11.9201 2 10.8V10M4.66667 6.66667L8 10L11.3333 6.66667M8 10V2"
                                stroke="#919EAB"
                                strokeWidth="1.4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </IconButton>
                </CustomTooltip>
            </Stack>
            {rightSlot}
        </GridToolbarContainer>
    );
}

export default function DataGridBrandInfringement({
    rows,
    loading,
    onTermClick,
    onTermsCellClick,
    onDownloadDomainReport,
    onExportAll,
    rightSlot,
    busyDomain = null,
    showSkeleton = false,
}: Props) {
    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});

    const handleColumnVisibilityModelChange = useCallback((newModel: GridColumnVisibilityModel) => {
        setColumnVisibilityModel(newModel);
    }, []);

    const [sortModel, setSortModel] = useState<GridSortModel>([
        { field: 'paid_presence_rate', sort: 'desc' },
    ]);

    // Controlled so the loading skeleton can be sized to a full page of rows.
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        pageSize: 10,
        page: 0,
    });

    const rowsWithId = useMemo(
        () => rows.map((row, index) => ({
            ...row,
            __rowId: `${row.domain_name}::${index}`,
            __busy: busyDomain === row.domain_name,
        })),
        [rows, busyDomain]
    );

    // MUI re-applies every colDef's `width` and the prop order whenever `columns` changes, undoing
    // the user's resizing and reordering. So the columns are built once: the handlers are read
    // through a ref at click time, and the busy domain travels on the rows.
    const handlersRef = useRef({ onTermClick, onTermsCellClick, onDownloadDomainReport });
    handlersRef.current = { onTermClick, onTermsCellClick, onDownloadDomainReport };

    const columns: GridColDef<InfringementRowWithId>[] = useMemo(() => [
        {
            field: 'domain_name',
            headerName: 'Infringing domains',
            flex: 1,
            minWidth: 180,
            renderCell: ({ row }) => (
                <Typography variant="body2" fontWeight={500} noWrap sx={{ color: '#333c47' }}>
                    {row.domain_name}
                </Typography>
            ),
        },
        {
            field: 'paid_presence_rate',
            headerName: 'Paid Presence Rate',
            width: 196,
            // Untyped, MUI filters this column with its *string* operators: `equals 97` compared
            // "97" against the raw 96.8 behind a cell reading "97%" and matched nothing, and the
            // numeric comparisons weren't even on offer. `type` restores them; the valueGetter
            // makes them compare against the integer on screen.
            type: 'number',
            // `type: 'number'` right-aligns a column by default, and this one is a value plus a
            // progress bar that reads left to right. Pinned back to where it was.
            align: 'left',
            headerAlign: 'left',
            valueGetter: (_value, row) => (row.paid_presence_rate == null
                ? null
                : presenceDisplayValue(row.paid_presence_rate)),
            renderCell: ({ row }) => (row.paid_presence_rate == null
                ? null
                : <PaidPresenceRateCell value={row.paid_presence_rate} />),
        },
        {
            field: 'active_days',
            headerName: 'Active days',
            width: 120,
            // Same reason as the rate above: without it these filter as text.
            type: 'number',
            align: 'right',
            headerAlign: 'right',
            renderCell: ({ row }) => (row.active_days == null
                ? null
                : <ActiveDaysGaugeCell activeDays={row.active_days} totalDays={row.days} />),
        },
        {
            field: 'unique_ads',
            headerName: 'Ads infringed',
            width: 132,
            type: 'number',
            align: 'right',
            headerAlign: 'right',
            renderCell: ({ row }) => (row.unique_ads == null
                ? null
                : <Typography variant="body2" fontWeight={500} color="#333C47">{row.unique_ads.toLocaleString()}</Typography>),
        },
        {
            field: 'place',
            headerName: 'Place',
            width: 229,
            sortable: false,
            // GridFilterInputSingleSelect only renders for a singleSelect column.
            type: 'singleSelect',
            // There is no `place` on the row: the chips are derived from the three *_count fields.
            // Without a valueGetter MUI resolved `row.place` to `undefined` and every filter on
            // this column matched zero rows.
            valueGetter: (_value, row) => placeLabels({
                titleOnly: row.title_only_count ?? 0,
                descOnly: row.desc_only_count ?? 0,
                both: row.both_count ?? 0,
            }),
            valueOptions: PLACE_OPTIONS as unknown as string[],
            filterOperators: placeFilterOperators,
            renderCell: ({ row }) => (
                <PlaceChipsCell
                    titleOnly={row.title_only_count ?? 0}
                    descOnly={row.desc_only_count ?? 0}
                    both={row.both_count ?? 0}
                />
            ),
        },
        {
            field: 'search_terms',
            headerName: 'Search terms',
            flex: 2,
            minWidth: 280,
            sortable: false,
            renderCell: ({ row }) => (
                <SearchTermsCell
                    terms={row.search_terms ?? []}
                    rates={row.search_term_rates ?? undefined}
                    onTermClick={(term) => handlersRef.current.onTermClick(term, row)}
                    onCellClick={() => handlersRef.current.onTermsCellClick(row)}
                />
            ),
        },
        {
            // Sorting on `status` groups the domains that have a report.
            field: 'status',
            headerName: 'Report',
            width: 104,
            sortable: true,
            renderCell: ({ row }) => (
                <ReportCell
                    hasReport={row.status === 'report_ready'}
                    busy={row.__busy}
                    onDownload={() => handlersRef.current.onDownloadDomainReport(row)}
                />
            ),
        },
    ], []);

    // The skeleton covers the card rather than replacing it: DataGridPro stays mounted underneath,
    // so its own state (filters, resized widths, column order) survives every refetch.
    return (
        <Box sx={{ position: 'relative', minHeight: showSkeleton ? CARD_HEIGHT : undefined }}>
            {showSkeleton && (
                <Skeleton
                    variant="rectangular"
                    animation="wave"
                    width="100%"
                    height="100%"
                    sx={{ position: 'absolute', inset: 0, zIndex: 1, borderRadius: '12px' }}
                />
            )}
            <Box
                // Frame with no padding of its own: like the other Signal grids, the toolbar, column
                // headers, rows and pagination all run edge to edge and carry their own insets.
                // `overflow: hidden` clips the grid's corners to the frame radius.
                bgcolor="white"
                sx={{
                    width: '100%',
                    // The skeleton is translucent: the grid under it must not show through.
                    visibility: showSkeleton ? 'hidden' : 'visible',
                    border: '1px solid rgba(145, 158, 171, 0.20)',
                    borderRadius: '12px',
                    // Figma: 0px 6px 12px 0px #919EAB0F.
                    boxShadow: '0px 6px 12px 0px rgba(145, 158, 171, 0.06)',
                    overflow: 'hidden',
                }}
            >
                <DataGridPro
                    rows={rowsWithId}
                    columns={columns}
                    getRowId={(row) => row.__rowId}
                    loading={loading}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                    columnHeaderHeight={36}
                    rowHeight={ROW_HEIGHT}
                    pagination
                    pageSizeOptions={[10, 25, 50]}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    // Every load is covered by the skeleton above, so MUI's own overlay never shows.
                    slots={{ toolbar: CustomToolbar as any }}
                    slotProps={{ toolbar: { onExportAll, rightSlot } as any }}
                    disableRowSelectionOnClick
                    autoHeight
                    sx={GRID_SX}
                />
            </Box>
        </Box>
    );
}
